import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    IconButton,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Checkbox,
    ListItemText,
    Autocomplete,
    useMediaQuery,
} from '@mui/material';
import JoditEditor from 'jodit-react';
import 'jodit/es5/jodit.min.css';
import { useWindowSize } from 'react-use';
import Confetti from 'react-confetti';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
    APP_DATETIME_FORMAT,
    formatAppDateTime,
    formatAppDateTimeApi,
    formatAppDateTimeOrDash,
    formatNoteEditorTimestamp,
} from '../utils/dateFormats';
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef, MRT_SortingState,
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_VisibilityState,
} from 'material-react-table';
import {TaskData, taskService} from '../api/tasks';
import { Task, PriorityOption, TaskWorkflowStatus, TASK_WORKFLOW_STATUSES } from '../types/entities/task.entity';
import { ActorChip } from './workflow/ActorChip';
import { AssigneeSelect } from './workflow/AssigneeSelect';
import { TagChips, TagsInput } from './workflow/TagsInput';
import { TaskComments } from './workflow/TaskComments';
import { WorkflowStatusSelect, useWorkflowStatusLabel } from './workflow/WorkflowStatus';
import { useActors } from '../hooks/useWorkspace';
import { projectsService, ProjectItem } from '../api/projects';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getNote, getNotes } from '../api/notes';
import { Note } from '../types/note';
import { useFocusSyncToast } from '../utils/useFocusSyncToast';
import { MarkerHighlightTextField, MarkerTableText } from '../utils/textMarkerHighlight';

/** Matches API `TaskPriorityEnum::MEDIUM`. */
const DEFAULT_TASK_PRIORITY = 40;

/** Narrow viewports (~Galaxy S24 portrait ≈ 360 CSS px): show checkbox, name, row actions only. */
const MOBILE_TASK_COLUMN_VISIBILITY: MRT_VisibilityState = {
    markAsDone: true,
    name: true,
    'mrt-row-actions': true,
    id: false,
    priority: false,
    status: false,
    created_at: false,
    updated_at: false,
    note: false,
    projects: false,
    due_date: false,
    completed_at: false,
    workflow_status: false,
    assignee: false,
    tags: false,
    created_by: false,
};

type NoteOption = Pick<Note, 'id' | 'name'>;

type TaskDescriptionQuickColorKey = 'green' | 'orange' | 'red';

/** Chip link on /projects — same taskId in URL must still reopen the modal. */
export const OPEN_TASK_FROM_PROJECTS_EVENT = 'aei-open-task-from-projects';

type TasksProps = {
    noteId?: string;
    projectId?: string;
    onTasksChanged?: () => void | Promise<void>;
    /** When Tasks is embedded on NoteForm, parent already loaded the note — skip duplicate GET /notes/{id}. */
    linkedNoteSummary?: { id: string; name: string };
};

export const Tasks: React.FC<TasksProps> = ({ noteId, projectId, onTasksChanged, linkedNoteSummary }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isMobile = useMediaQuery('(max-width:960px)');
    const { width, height } = useWindowSize();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const descriptionEditorRef = useRef<any>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const [runConfetti, setRunConfetti] = useState<boolean>(false);
    const [horizontalScrollDirection, setHorizontalScrollDirection] = useState<-1 | 0 | 1>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isClosingModal, setIsClosingModal] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDescLargeOpen, setIsDescLargeOpen] = useState(false);
    const [markAllTodoLoading, setMarkAllTodoLoading] = useState(false);

    const descriptionEditorConfig = useMemo(
        () => ({
            readonly: false,
            height: 260,
            toolbarAdaptive: false,
            showCharsCounter: false,
            showWordsCounter: false,
            showXPathInStatusbar: false,
            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            defaultActionOnPaste: 'insert_clear_html' as const,
            buttons: [
                'bold',
                'italic',
                'underline',
                '|',
                'ul',
                'ol',
                '|',
                'link',
                'table',
                '|',
                'undo',
                'redo',
                '|',
                'source',
            ],
        }),
        [],
    );

    const descriptionQuickPalette = useMemo(
        () => ({
            text: {
                green: '#1f5d2a',
                orange: '#8a4b00',
                red: '#8a1f1f',
            },
            background: {
                green: '#c8e6c9',
                orange: '#ffd8a8',
                red: '#f5c2c7',
            },
        }),
        [],
    );

    const descriptionQuickColorOptions = useMemo(
        () => [
            { key: 'green' as const, label: 'Green', hotkey: 'G' },
            { key: 'orange' as const, label: 'Orange', hotkey: 'O' },
            { key: 'red' as const, label: 'Red', hotkey: 'R' },
        ],
        [],
    );

    // Autosave
    const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoSaveResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSavedPayloadKeyRef = useRef<string>('');

    // Description HTML preview
    const [descPreviewMode, setDescPreviewMode] = useState<'rendered' | 'raw'>('rendered');
    const descPreviewRef = useRef<HTMLDivElement | null>(null);
    const [descPreviewSelection, setDescPreviewSelection] = useState<{
        text: string;
        top: number;
        left: number;
    } | null>(null);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [formData, setFormData] = useState<{
        name: string;
        description: string;
        priority: number | null;
        status: boolean;
        noteId?: string;
        note_ids: string[];
        project_ids: string[];
        due_date: Date | null;
        completed_at: Date | null;
        /** Optional: only sent when set (modal); inline cell edits leave them untouched on the server. */
        workflow_status?: TaskWorkflowStatus;
        assignee?: string | null;
        tags?: string[];
    }>({
        name: '',
        description: '',
        priority: DEFAULT_TASK_PRIORITY,
        status: false,
        noteId: noteId ?? undefined,
        note_ids: [],
        project_ids: [],
        due_date: null,
        completed_at: null,
    });
    const [errors, setErrors] = useState({
        name: '',
        description: '',
        priority: ''
    });

    const [availableNotes, setAvailableNotes] = useState<NoteOption[]>([]);
    const [notesSearchInput, setNotesSearchInput] = useState('');
    const [notesSearchOptions, setNotesSearchOptions] = useState<NoteOption[]>([]);
    const [notesSearchLoading, setNotesSearchLoading] = useState(false);
    const notesSearchTimeout = useRef<number | null>(null);
    const [availableProjects, setAvailableProjects] = useState<ProjectItem[]>([]);
    const [taskPriorities, setTaskPriorities] = useState<PriorityOption[]>([]);

    const mergeNotesIntoAvailable = (notesToMerge: Array<{ id: string; name: string }>) => {
        if (!notesToMerge.length) {
            return;
        }

        setAvailableNotes((prev) => {
            const existingIds = new Set(prev.map((note) => String(note.id)));
            const missing = notesToMerge
                .filter((note) => !!note?.id && !!note?.name)
                .filter((note) => !existingIds.has(String(note.id)))
                .map((note) => ({ id: String(note.id), name: note.name }));

            if (!missing.length) {
                return prev;
            }

            return [...prev, ...missing];
        });
    };

    useEffect(() => {
        const fetchNotesSearch = async () => {
            if (!notesSearchInput || notesSearchInput.trim().length === 0) {
                setNotesSearchOptions([]);
                setNotesSearchLoading(false);
                return;
            }

            setNotesSearchLoading(true);
            try {
                const filters = `filters[name][$contains]=${encodeURIComponent(notesSearchInput)}`;
                const data = await getNotes({ filters, per_page: 20 });
                setNotesSearchOptions(data.notes || []);
            } catch {
                setNotesSearchOptions([]);
            } finally {
                setNotesSearchLoading(false);
            }
        };

        if (notesSearchTimeout.current) {
            window.clearTimeout(notesSearchTimeout.current);
        }

        notesSearchTimeout.current = window.setTimeout(fetchNotesSearch, notesSearchInput ? 300 : 0) as unknown as number;

        return () => {
            if (notesSearchTimeout.current) {
                window.clearTimeout(notesSearchTimeout.current);
            }
        };
    }, [notesSearchInput]);

    useEffect(() => {
        if (noteId) {
            return;
        }
        // Do not override the tab title when Tasks is embedded (e.g. /projects, notes).
        const path = location.pathname.replace(/\/$/, '') || '/';
        const isStandaloneTasksRoute = /\/tasks$/.test(path);
        if (!isStandaloneTasksRoute) {
            return;
        }
        document.title = t('tasks.title');
    }, [location.pathname, noteId, t]);

    useEffect(() => {
        if (!noteId) {
            return;
        }

        const seedNote = (note: { id: string; name: string }) => {
            setAvailableNotes((prev) => {
                const alreadyExists = prev.some((item) => String(item.id) === String(note.id));
                if (alreadyExists) {
                    return prev;
                }

                return [
                    ...prev,
                    {
                        id: note.id,
                        name: note.name,
                        description: '',
                        created_at: '',
                        updated_at: '',
                    },
                ];
            });
        };

        if (linkedNoteSummary && String(linkedNoteSummary.id) === String(noteId)) {
            seedNote(linkedNoteSummary);
            return;
        }

        getNote(noteId)
            .then((note) => {
                seedNote(note);
            })
            .catch(() => {
                // Keep create/edit modal usable even if note details fail to load.
            });
    }, [noteId, linkedNoteSummary]);

    useEffect(() => {
        projectsService.getCatalog().then((projects) => {
            setAvailableProjects(projects);
        });
    }, []);

    useEffect(() => {
        taskService.getEnums().then((priorities) => {
            setTaskPriorities(priorities);
        });
    }, []);

    const LOCAL_STORAGE_FILTERS_KEY = 'tasks_column_filters';
    const LOCAL_STORAGE_COLUMN_VISIBILITY_KEY = 'tasks_column_visibility';

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(() => {
        if (!noteId) {
            const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
            if (saved) {
                try {
                    return JSON.parse(saved);
                } catch {
                    return [];
                }
            }
        }
        return [];
    });
    const [globalFilter, setGlobalFilter] = useState('');
    /** Main /tasks: priority then last update. Note embed: todo (status false) first, then same tie-breakers. */
    const [sorting, setSorting] = useState<MRT_SortingState>(() =>
        noteId
            ? [
                  { id: 'status', desc: false },
                  { id: 'priority', desc: false },
                  { id: 'updated_at', desc: true },
              ]
            : [
                  { id: 'priority', desc: false },
                  { id: 'updated_at', desc: true },
              ],
    );
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 15,
    });

    const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(() => {
        if (!noteId) {
            const saved = localStorage.getItem(LOCAL_STORAGE_COLUMN_VISIBILITY_KEY);
            if (saved) {
                try {
                    return JSON.parse(saved);
                } catch {
                    return {};
                }
            }
        }
        return {};
    });

    useLayoutEffect(() => {
        if (isMobile) {
            setColumnVisibility((prev) => ({
                ...prev,
                ...MOBILE_TASK_COLUMN_VISIBILITY,
            }));
            return;
        }
        if (noteId) {
            setColumnVisibility({});
            return;
        }
        const saved = localStorage.getItem(LOCAL_STORAGE_COLUMN_VISIBILITY_KEY);
        if (saved) {
            try {
                setColumnVisibility(JSON.parse(saved));
            } catch {
                setColumnVisibility({});
            }
        } else {
            setColumnVisibility({});
        }
    }, [isMobile, noteId]);

    const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
    const validateRequired = (value: string) => !!value.length;
    const [taskIsEdit, setTaskIsEdit] = useState<boolean>(false);
    const prevTaskIdFromQueryRef = useRef('');
    const handledNewTaskQueryRef = useRef<string | null>(null);

    const selectedTaskIdFromQuery = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return (params.get('taskId') ?? '').trim();
    }, [location.search]);

    const selectedProjectIdFromQuery = useMemo(() => {
        if (projectId?.trim()) {
            return projectId.trim();
        }
        const params = new URLSearchParams(location.search);
        return (params.get('projectId') ?? '').trim();
    }, [location.search, projectId]);

    const projectNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const project of availableProjects) {
            if (!project?.id) {
                continue;
            }
            map.set(project.id, project.name?.trim() || project.id);
        }
        return map;
    }, [availableProjects]);

    const startConfetti = () => {
        setRunConfetti(true);
    }

    useEffect(() => {
        if (horizontalScrollDirection === 0) {
            return;
        }

        const interval = window.setInterval(() => {
            const container = tableContainerRef.current;
            if (!container) {
                return;
            }

            container.scrollLeft += horizontalScrollDirection * 14;
        }, 16);

        return () => {
            window.clearInterval(interval);
        };
    }, [horizontalScrollDirection]);

    const {
        data: { tasks = [], meta } = {},
        isError,
        isRefetching,
        isLoading,
        refetch,
        dataUpdatedAt,
    } = useQuery<TaskData>({
        queryKey: [
            'tasks-list',
            {
                columnFilters,
                globalFilter,
                pagination,
                sorting,
                noteId,
                selectedProjectIdFromQuery,
            },
        ],
        queryFn: async () => {
            let filters = '';
            if (columnFilters) {
                filters = buildFilterParams(columnFilters)
            }
            if (selectedProjectIdFromQuery) {
                filters += (filters ? '&' : '') + `project_id=${encodeURIComponent(selectedProjectIdFromQuery)}`;
            }
            let sort = '';
            if (sorting) {
                sort = buildSortParams(sorting)
            }
            return taskService.getAll(
                pagination.pageIndex + 1,
                pagination.pageSize,
                filters,
                sort,
                noteId,
            )
        },
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    useFocusSyncToast({
        dataUpdatedAt,
        isRefetching,
        message: 'Tasks synced from another window',
    });

    const { data: actors = [] } = useActors();
    const workflowStatusLabel = useWorkflowStatusLabel();

    /** Inline workflow change from the table (PATCH, other fields untouched). */
    const handleQuickWorkflowChange = async (task: Task, workflow_status: TaskWorkflowStatus) => {
        try {
            await taskService.patch(String(task.id), { workflow_status });
            if (workflow_status === 'done' && !task.status) {
                startConfetti();
            }
            await queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
            await queryClient.invalidateQueries({ queryKey: ['tasks'] });
            await onTasksChanged?.();
        } catch {
            enqueueSnackbar(t('tasks.updateError'), { variant: 'error' });
        }
    };

    const columns = useMemo<MRT_ColumnDef<Task>[]>(
        () => [
            {
                id: 'markAsDone',
                header: t('tasks.markAsDone'),
                Cell: ({ row }) => {
                    const isDone = row.original.status;
                    return (
                        <Checkbox
                            checked={isDone}
                            title={isDone ? t('tasks.markAsTodo') : t('tasks.markAsDone')}
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                            onChange={(event) => {
                                event.stopPropagation();
                                const newStatus = !isDone;
                                setFormData({
                                    name: row.original.name,
                                    status: newStatus,
                                    priority: row.original.priority?.value ?? null,
                                    description: row.original.description,
                                    noteId: noteId ?? undefined,
                                    note_ids: row.original.notes?.map(n => n.id) ?? [],
                                    project_ids: row.original.project_ids ?? [],
                                    due_date: row.original.due_date ? new Date(row.original.due_date) : null,
                                    completed_at: newStatus && row.original.completed_at
                                        ? new Date(row.original.completed_at)
                                        : null,
                                });
                                setSelectedTask(row.original);
                                setTaskIsEdit(true);

                                if (newStatus) {
                                    startConfetti();
                                }
                            }}
                        />
                    );
                },
                enableSorting: false,
                enableColumnFilter: false,
                enableEditing: false,
                size: isMobile ? 52 : 50,
                grow: isMobile ? 0 : undefined,
            },
            {
                accessorKey: 'id',
                header: 'ID',
                enableEditing: false,
            },
            {
                accessorKey: 'name',
                header: t('tasks.name'),
                grow: isMobile ? 1 : undefined,
                Cell: ({ cell }) => {
                    const value = String(cell.getValue() ?? '').trim();
                    return value ? <MarkerTableText text={value} /> : '—';
                },
                muiTableBodyCellProps: isMobile
                    ? {
                          sx: {
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              py: 1,
                              verticalAlign: 'top',
                          },
                      }
                    : undefined,
                muiEditTextFieldProps: ({ row }) => ({
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.name,
                    helperText: validationErrors?.name,
                    onBlur: (event) => {
                        const validationError = !validateRequired(event.currentTarget.value)
                            ? 'Required'
                            : undefined;
                        setValidationErrors({
                            ...validationErrors,
                            name: validationError,
                        });
                        setFormData({
                            name: event.currentTarget.value,
                            status: row.original.status,
                            priority: row.original.priority?.value ?? null,
                            description: row.original.description,
                            noteId: noteId ?? undefined,
                            note_ids: row.original.notes?.map(n => n.id) ?? [],
                            project_ids: row.original.project_ids ?? [],
                            due_date: row.original.due_date ? new Date(row.original.due_date) : null,
                            completed_at: row.original.completed_at ? new Date(row.original.completed_at) : null,
                        });
                        setSelectedTask(row.original);
                        setTaskIsEdit(true);
                    },
                }),
            },
            {
                accessorKey: 'priority',
                accessorFn: (row) => row.priority?.value ?? null,
                header: t('tasks.priority'),
                Cell: ({ row }) => row.original.priority?.label || '—',
                filterVariant: 'select',
                filterSelectOptions: taskPriorities.map(p => ({ label: p.label, value: String(p.value) })),
                editVariant: 'select',
                editSelectOptions: taskPriorities.map(p => ({ label: p.label, value: String(p.value) })),
                muiEditTextFieldProps: ({ row }) => ({
                    select: true,
                    error: !!validationErrors?.priority,
                    helperText: validationErrors?.priority,
                    value: String(row.original.priority?.value ?? ''),
                    onChange: (event) => {
                        const priorityValue = parseInt(event.target.value);
                        setFormData({
                            name: row.original.name,
                            status: row.original.status,
                            priority: priorityValue,
                            description: row.original.description,
                            noteId: noteId ?? undefined,
                            note_ids: row.original.notes?.map(n => n.id) ?? [],
                            project_ids: row.original.project_ids ?? [],
                            due_date: row.original.due_date ? new Date(row.original.due_date) : null,
                            completed_at: row.original.completed_at ? new Date(row.original.completed_at) : null,
                        });
                        setSelectedTask(row.original);
                        setTaskIsEdit(true);
                    }
                }),
            },
            {
                id: 'status',
                header: t('tasks.status'),
                accessorKey: 'status',
                Cell: ({ cell }) => cell.getValue() ? t('tasks.active') : t('tasks.inactive'),
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: t('tasks.active'), value: '1' },
                    { label: t('tasks.inactive'), value: '0' },
                ],
                editVariant: 'select',
                editSelectOptions: [
                    { label: t('tasks.active'), value: '1' },
                    { label: t('tasks.inactive'), value: '0' },
                ],
                muiEditTextFieldProps: ({ row }) => ({
                    select: true,
                    value: String(row.original.status ? 1 : 0),
                    error: !!validationErrors?.status,
                    helperText: validationErrors?.status,
                    onChange: (event) => {
                        setFormData({
                            name: row.original.name,
                            status: event.target.value === '1',
                            priority: row.original.priority?.value ?? null,
                            description: row.original.description,
                            noteId: noteId ?? undefined,
                            note_ids: row.original.notes?.map(n => n.id) ?? [],
                            project_ids: row.original.project_ids ?? [],
                            due_date: row.original.due_date ? new Date(row.original.due_date) : null,
                            completed_at: row.original.completed_at ? new Date(row.original.completed_at) : null,
                        });
                        setSelectedTask(row.original);
                        setTaskIsEdit(true);
                    }
                }),
            },
            {
                id: 'workflow_status',
                accessorFn: (row) => row.workflow_status ?? (row.status ? 'done' : 'todo'),
                header: t('workflow.statusLabel'),
                filterVariant: 'select',
                filterSelectOptions: TASK_WORKFLOW_STATUSES.map((status) => ({ label: workflowStatusLabel(status), value: status })),
                enableEditing: false,
                Cell: ({ row }) => (
                    <Box onClick={(event) => event.stopPropagation()}>
                        <WorkflowStatusSelect
                            size="small"
                            label=""
                            fullWidth={false}
                            value={row.original.workflow_status ?? (row.original.status ? 'done' : 'todo')}
                            onChange={(workflow_status) => void handleQuickWorkflowChange(row.original, workflow_status)}
                        />
                    </Box>
                ),
            },
            {
                id: 'assignee',
                accessorFn: (row) => row.assignee?.name ?? '',
                header: t('workflow.assignee'),
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: t('workflow.unassigned'), value: 'none' },
                    ...actors.map((actor) => ({ label: actor.name ?? actor.key, value: actor.key })),
                ],
                enableSorting: false,
                enableEditing: false,
                Cell: ({ row }) => <ActorChip actor={row.original.assignee} />,
            },
            {
                id: 'tags',
                accessorFn: (row) => (row.tags ?? []).map((tag) => tag.name).join(', '),
                header: t('tags.title'),
                enableSorting: false,
                enableEditing: false,
                Cell: ({ row }) => (
                    <TagChips
                        tags={row.original.tags}
                        onClick={(name) => setColumnFilters((prev) => [
                            ...prev.filter((filter) => filter.id !== 'tags'),
                            { id: 'tags', value: name },
                        ])}
                    />
                ),
            },
            {
                id: 'created_by',
                accessorFn: (row) => row.created_by?.name ?? '',
                header: t('workflow.createdBy'),
                enableSorting: false,
                enableColumnFilter: false,
                enableEditing: false,
                Cell: ({ row }) => <ActorChip actor={row.original.created_by} />,
            },
            {
                id: 'created_at',
                accessorFn: (row) => new Date(row.created_at),
                header: t('tasks.createdAt'),
                filterVariant: 'date-range',
                Cell: ({ cell }) => formatAppDateTime(cell.getValue<Date>()),
                enableEditing: false,
                sortingFn: 'datetime',
            },
            {
                id: 'updated_at',
                accessorFn: (row) => new Date(row.updated_at),
                header: t('tasks.updatedAt'),
                filterVariant: 'date-range',
                Cell: ({ cell }) => formatAppDateTime(cell.getValue<Date>()),
                enableEditing: false,
                sortingFn: 'datetime',
            },
            {
                id: 'note',
                accessorFn: (row) => row.notes?.map(n => n.name).join(', ') || '',
                header: t('tasks.note'),
                Cell: ({ row }) => {
                    const notes = row.original.notes ?? [];
                    if (notes.length === 0) {
                        return <span style={{ color: '#999' }}>—</span>;
                    }
                    return (
                        <>
                            {notes.map((note, index) => (
                                <span key={note.id}>
                                    <Link
                                        to={`/notes/${note.id}`}
                                        target="_blank"
                                    >
                                        {note.name}
                                    </Link>
                                    {index < notes.length - 1 && <br />}
                                </span>
                            ))}
                        </>
                    );
                },
                enableSorting: false,
                enableColumnFilter: false,
                enableEditing: false,
            },
            {
                id: 'projects',
                accessorFn: (row) => (row.project_ids ?? [])
                    .map((projectId) => projectNameById.get(projectId) || projectId)
                    .join(', '),
                header: 'Projects',
                Cell: ({ row }) => {
                    const projectIds = row.original.project_ids ?? [];
                    if (projectIds.length === 0) {
                        return '—';
                    }

                    return (
                        <>
                            {projectIds.map((projectId, index) => (
                                <span key={projectId}>
                                    <Link to={`/projects?projectId=${encodeURIComponent(projectId)}`} target="_blank">
                                        {projectNameById.get(projectId) || projectId}
                                    </Link>
                                    {index < projectIds.length - 1 && <br />}
                                </span>
                            ))}
                        </>
                    );
                },
                enableSorting: false,
                enableEditing: false,
            },
            {
                id: 'due_date',
                accessorFn: (row) => {
                    return row.due_date ? new Date(row.due_date) : null;
                },
                header: t('tasks.dueDate'),
                filterVariant: 'date-range',
                Cell: ({ cell }) => {
                    const value = cell.getValue<Date | null>();
                    return formatAppDateTimeOrDash(value);
                },
                enableEditing: false
            },
            {
                id: 'completed_at',
                accessorFn: (row) => {
                    return row.completed_at ? new Date(row.completed_at) : null;
                },
                header: t('tasks.completedAt'),
                filterVariant: 'date-range',
                Cell: ({ cell }) => {
                    const value = cell.getValue<Date | null>();
                    return formatAppDateTimeOrDash(value);
                },
                enableEditing: false
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [projectNameById, taskIsEdit, validationErrors, taskPriorities, t, isMobile, actors],
    );

    const table = useMaterialReactTable({
        columns,
        data: tasks,
        initialState: {
            showColumnFilters: true,
            columnPinning: { right: ['mrt-row-actions'] },
        },
        enableGlobalFilter: false,
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        enableMultiSort: true,
        layoutMode: isMobile ? 'grid' : 'semantic',
        enableRowActions: true,
        positionActionsColumn: 'last',
        editDisplayMode: 'cell',
        enableCellActions: true,
        enableClickToCopy: 'context-menu',
        enableEditing: true,
        muiTableContainerProps: {
            ref: tableContainerRef,
            onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => {
                const container = tableContainerRef.current;
                if (!container) {
                    return;
                }

                const rect = container.getBoundingClientRect();
                const x = event.clientX - rect.left;
                const edgeThreshold = 56;

                if (x >= rect.width - edgeThreshold) {
                    setHorizontalScrollDirection(1);
                    return;
                }

                if (x <= edgeThreshold) {
                    setHorizontalScrollDirection(-1);
                    return;
                }

                if (horizontalScrollDirection !== 0) {
                    setHorizontalScrollDirection(0);
                }
            },
            onMouseLeave: () => setHorizontalScrollDirection(0),
            sx: { overflowX: 'auto' },
        },
        muiToolbarAlertBannerProps: isError
            ? {
                color: 'error',
                children: t('common.errorFetchingData'),
            }
            : undefined,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onColumnVisibilityChange: setColumnVisibility,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        renderTopToolbarCustomActions: () => (
            <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip arrow title={t('common.refreshData')}>
                    <span>
                        <IconButton onClick={() => refetch()} disabled={markAllTodoLoading}>
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                {noteId ? (
                    <Tooltip title={t('tasks.markAllAsTodoTooltip')}>
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={markAllTodoLoading || isLoading}
                                onClick={() => void handleMarkAllAsTodoForNote()}
                            >
                                {t('tasks.markAllAsTodo')}
                            </Button>
                        </span>
                    </Tooltip>
                ) : null}
            </Stack>
        ),
        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 2, flexShrink: 0 }}>
                <Tooltip title="Edit">
                    <IconButton onClick={(event) => {
                        event.stopPropagation();
                        handleOpenModal(row.original);
                    }}>
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton color="error" onClick={(event) => {
                        event.stopPropagation();
                        handleDelete(row.original);
                    }}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => {
                handleOpenModal(row.original);
            },
            sx: { cursor: 'pointer' },
        }),
        rowCount: meta?.total ?? 0,
        state: {
            columnFilters,
            columnVisibility,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            sorting,
        },
    });

    const buildFilterParams = (filters: MRT_ColumnFiltersState) => {
        return filters
            .filter(filter => {
                if (filter.value === undefined || filter.value === null) return false;
                if (Array.isArray(filter.value)) return filter.value.some(v => v !== undefined && v !== null);
                return true;
            })
            .map((filter) => {
                // Workflow fields use dedicated API query params.
                if (filter.id === 'workflow_status' || filter.id === 'assignee' || filter.id === 'tags') {
                    const raw = Array.isArray(filter.value) ? filter.value.join(',') : String(filter.value ?? '');
                    if (!raw.trim()) return '';
                    const param = filter.id === 'tags' ? 'tag' : filter.id;
                    return `${param}=${encodeURIComponent(raw.trim())}`;
                }
                let operator = '$contains';
                let value: string | string[] = '';

                if (filter.id === 'created_at' || filter.id === 'updated_at' || filter.id === 'age') {
                    if (Array.isArray(filter.value)) {
                        const values = filter.value
                            .filter(v => v !== undefined && v !== null)
                            .map(v => {
                                if (filter.id === 'created_at' || filter.id === 'updated_at') {
                                    const date = v instanceof Date ? v : new Date(v);
                                    return `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                                } else {
                                    return v.toString();
                                }
                            });

                        if (values.length === 2) {
                            operator = '$between';
                            value = values;
                        } else if (values.length === 1) {
                            if (filter.value[0] && !filter.value[1]) {
                                operator = '$gte';
                                value = values[0];
                            } else if (!filter.value[0] && filter.value[1]) {
                                operator = '$lte';
                                value = values[0];
                            } else {
                                return '';
                            }
                        } else {
                            return '';
                        }
                    } else {
                        return '';
                    }
                }
                else if (filter.id === 'priority') {
                    value = Array.isArray(filter.value)
                        ? filter.value.map(v => {
                            const num = typeof v === 'string' ? parseInt(v, 10) : (typeof v === 'number' ? v : 0);
                            return isNaN(num) ? '' : num.toString();
                        }).filter(v => v).join(',')
                        : (() => {
                            const num = typeof filter.value === 'string' ? parseInt(filter.value, 10) : (typeof filter.value === 'number' ? filter.value : 0);
                            return isNaN(num) ? '' : num.toString();
                        })();
                }
                else {
                    value = Array.isArray(filter.value)
                        ? filter.value.join(',')
                        : filter.value?.toString() || '';
                }

                if (!value) return '';

                if (operator === '$between' && Array.isArray(value) && value.length === 2) {
                    return [
                        `filters[${filter.id}][$betweenDate][0]=${encodeURIComponent(value[0])}`,
                        `filters[${filter.id}][$betweenDate][1]=${encodeURIComponent(value[1])}`
                    ].join('&');
                }

                const key = `filters[${filter.id}][${operator}]`;
                return `${key}=${encodeURIComponent(value.toString())}`;
            })
            .filter(Boolean)
            .join('&');
    };

    const buildSortParams = (sortItems: MRT_SortingState) => {
        return sortItems
            .map((sort, index) => {
                const direction = sort.desc ? ":desc" : ":asc";
                return `sort[${index}]=${sort.id}${direction}`;
            })
            .join("&");
    }

    const validateForm = () => {
        const newErrors = {
            name: '',
            description: '',
            priority: ''
        };
        let isValid = true;

        if (!formData.name.trim()) {
            newErrors.name = t('validation.required');
            isValid = false;
        }
        if (formData.priority == null) {
            newErrors.priority = t('validation.required');
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const canAutosaveTask = (data: typeof formData) =>
        Boolean(data.name.trim() && data.priority != null);

    const serializeTaskRequestPayload = (data: typeof formData, isUpdate: boolean) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dataToSend: any = { ...data };
        dataToSend.description = typeof dataToSend.description === 'string' ? dataToSend.description : '';
        if (!data.noteId) {
            delete dataToSend.noteId;
        }
        if (!dataToSend.note_ids || dataToSend.note_ids.length === 0) {
            delete dataToSend.note_ids;
        }
        if (!dataToSend.project_ids) {
            dataToSend.project_ids = [];
        }
        if (isUpdate) {
            dataToSend.due_date = formatAppDateTimeApi(data.due_date);
        } else if (dataToSend.due_date) {
            dataToSend.due_date = formatAppDateTimeApi(data.due_date);
        } else {
            delete dataToSend.due_date;
        }
        if (!isUpdate) {
            delete dataToSend.completed_at;
        } else if (dataToSend.completed_at) {
            dataToSend.completed_at = formatAppDateTimeApi(data.completed_at);
        } else {
            delete dataToSend.completed_at;
        }
        return dataToSend;
    };

    const closeModalImmediately = useCallback(() => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        if (autoSaveResetTimerRef.current) {
            clearTimeout(autoSaveResetTimerRef.current);
            autoSaveResetTimerRef.current = null;
        }
        setAutoSaveStatus('idle');
        setIsClosingModal(false);
        setIsModalOpen(false);
        setSelectedTask(null);
        setDescPreviewSelection(null);
        setFormData({
            name: '',
            description: '',
            priority: DEFAULT_TASK_PRIORITY,
            status: true,
            noteId: noteId ?? undefined,
            note_ids: [],
            project_ids: [],
            due_date: null,
            completed_at: null,
        });
        setErrors({
            name: '',
            description: '',
            priority: '',
        });
    }, [noteId]);

    const createMutation = useMutation({
        mutationFn: taskService.create,
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, task }: { id: string; task: Parameters<typeof taskService.update>[1] }) =>
            taskService.update(id, task),
    });

    const deleteMutation = useMutation({
        mutationFn: taskService.delete,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
            setIsDeleteModalOpen(false);
            setSelectedTask(null);
            await queryClient.invalidateQueries({ queryKey: ['tasks'] });
            await queryClient.invalidateQueries({ queryKey: ['projects-catalog'] });
            await onTasksChanged?.();
            enqueueSnackbar(t('tasks.deleteSuccess'), { variant: 'success' });
        },
        onError: () => {
            enqueueSnackbar(t('tasks.deleteError'), { variant: 'error' });
        }
    });

    const handleMarkAllAsTodoForNote = useCallback(async () => {
        if (!noteId) {
            return;
        }
        setMarkAllTodoLoading(true);
        try {
            const allTasks: Task[] = [];
            let page = 1;
            let lastPage = 1;
            const perPage = 100;
            do {
                const batch = await taskService.getAll(page, perPage, '', '', noteId);
                allTasks.push(...batch.tasks);
                lastPage = batch.meta.lastPage;
                page += 1;
            } while (page <= lastPage);

            const doneTasks = allTasks.filter((task) => task.status);
            if (doneTasks.length === 0) {
                enqueueSnackbar(t('tasks.markAllAsTodoNone'), { variant: 'info' });
                return;
            }

            let updatedCount = 0;
            for (const task of doneTasks) {
                const priorityVal = task.priority?.value;
                if (priorityVal == null) {
                    continue;
                }
                await taskService.update(String(task.id), {
                    name: task.name,
                    description: task.description,
                    priority: priorityVal,
                    status: false,
                    note_ids: task.notes?.map((n) => n.id) ?? [],
                    project_ids: task.project_ids ?? [],
                    ...(task.due_date ? { due_date: task.due_date } : {}),
                });
                updatedCount += 1;
            }

            if (updatedCount === 0) {
                enqueueSnackbar(t('tasks.markAllAsTodoError'), { variant: 'error' });
                return;
            }

            await queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
            await queryClient.invalidateQueries({ queryKey: ['tasks'] });
            await onTasksChanged?.();
            void refetch();
            enqueueSnackbar(t('tasks.markAllAsTodoSuccess', { count: updatedCount }), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('tasks.markAllAsTodoError'), { variant: 'error' });
        } finally {
            setMarkAllTodoLoading(false);
        }
    }, [noteId, enqueueSnackbar, t, queryClient, onTasksChanged, refetch]);

    const buildAutoSavePayloadKey = useCallback((data: typeof formData) =>
        JSON.stringify({
            name: data.name.trim(),
            description: data.description,
            priority: data.priority,
            status: data.status,
            note_ids: [...data.note_ids].sort(),
            project_ids: [...data.project_ids].sort(),
            due_date: formatAppDateTimeApi(data.due_date),
            workflow_status: data.workflow_status,
            assignee: data.assignee,
            tags: data.tags ? [...data.tags].sort() : undefined,
        }),
    []);

    const persistTask = useCallback(
        async (
            data: typeof formData,
            options: { closeOnSuccess: boolean; showSuccessToast: boolean; isAutoSave: boolean },
        ): Promise<boolean> => {
            if (!canAutosaveTask(data)) {
                return false;
            }

            if (createMutation.isPending || updateMutation.isPending) {
                return false;
            }

            const payloadKey = buildAutoSavePayloadKey(data);
            if (options.isAutoSave && payloadKey === lastSavedPayloadKeyRef.current) {
                return true;
            }

            const isUpdate = Boolean(selectedTask?.id);
            const dataToSend = serializeTaskRequestPayload(data, isUpdate);
            const wasUpdate = isUpdate;

            if (options.isAutoSave) {
                setAutoSaveStatus('saving');
            }

            try {
                let savedTask: Task;
                if (selectedTask?.id) {
                    savedTask = await updateMutation.mutateAsync({ id: selectedTask.id, task: dataToSend });
                } else {
                    savedTask = await createMutation.mutateAsync(dataToSend);
                }

                lastSavedPayloadKeyRef.current = payloadKey;
                setSelectedTask(savedTask);
                mergeNotesIntoAvailable((savedTask.notes ?? []).map((n) => ({ id: String(n.id), name: n.name })));

                await queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
                await queryClient.invalidateQueries({ queryKey: ['tasks'] });
                await queryClient.invalidateQueries({ queryKey: ['projects-catalog'] });
                await onTasksChanged?.();

                if (options.isAutoSave) {
                    setAutoSaveStatus('saved');
                    if (autoSaveResetTimerRef.current) {
                        clearTimeout(autoSaveResetTimerRef.current);
                    }
                    autoSaveResetTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 1200);
                }

                if (options.showSuccessToast) {
                    enqueueSnackbar(wasUpdate ? t('tasks.updateSuccess') : t('tasks.createSuccess'), {
                        variant: 'success',
                    });
                }

                if (options.closeOnSuccess) {
                    closeModalImmediately();
                }

                return true;
            } catch {
                if (options.isAutoSave) {
                    setAutoSaveStatus('error');
                } else {
                    enqueueSnackbar(wasUpdate ? t('tasks.updateError') : t('tasks.createError'), {
                        variant: 'error',
                    });
                }
                return false;
            }
        },
        [
            buildAutoSavePayloadKey,
            closeModalImmediately,
            createMutation,
            enqueueSnackbar,
            mergeNotesIntoAvailable,
            onTasksChanged,
            queryClient,
            selectedTask,
            t,
            updateMutation,
        ],
    );

    const triggerAutoSaveNow = useCallback(
        async (dataOverride?: typeof formData) => {
            if (!isModalOpen) {
                return;
            }
            const data = dataOverride ?? formData;
            if (!canAutosaveTask(data)) {
                return;
            }

            const key = buildAutoSavePayloadKey(data);
            if (key === lastSavedPayloadKeyRef.current) {
                return;
            }

            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
            }

            await persistTask(data, { closeOnSuccess: false, showSuccessToast: false, isAutoSave: true });
        },
        [buildAutoSavePayloadKey, formData, isModalOpen, persistTask],
    );

    // Debounced autosave (create or update), same cadence as External Assets
    useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        if (!canAutosaveTask(formData)) {
            return;
        }

        const key = buildAutoSavePayloadKey(formData);
        if (key === lastSavedPayloadKeyRef.current) {
            return;
        }

        if (createMutation.isPending || updateMutation.isPending) {
            return;
        }

        setAutoSaveStatus('pending');
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(() => {
            void triggerAutoSaveNow();
        }, 900);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, isModalOpen, createMutation.isPending, updateMutation.isPending, triggerAutoSaveNow, buildAutoSavePayloadKey]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
            if (autoSaveResetTimerRef.current) clearTimeout(autoSaveResetTimerRef.current);
        };
    }, []);

    const hideDescPreviewSelection = useCallback(() => {
        setDescPreviewSelection(null);
    }, []);

    const updateDescPreviewSelection = useCallback(() => {
        const container = descPreviewRef.current;
        const selection = typeof window !== 'undefined' ? window.getSelection() : null;
        if (!container || !selection || selection.rangeCount === 0 || selection.isCollapsed) {
            setDescPreviewSelection(null);
            return;
        }
        const selectedText = selection.toString().trim();
        if (!selectedText) { setDescPreviewSelection(null); return; }

        const range = selection.getRangeAt(0);
        const commonAncestor = range.commonAncestorContainer;
        const targetNode = commonAncestor.nodeType === Node.TEXT_NODE ? commonAncestor.parentNode : commonAncestor;
        if (!(targetNode instanceof Node) || !container.contains(targetNode)) {
            setDescPreviewSelection(null); return;
        }

        const rangeRect = range.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        if (!rangeRect.width && !rangeRect.height) { setDescPreviewSelection(null); return; }

        setDescPreviewSelection({
            text: selectedText,
            left: rangeRect.left - containerRect.left + container.scrollLeft + rangeRect.width / 2,
            top: rangeRect.top - containerRect.top + container.scrollTop - 12,
        });
    }, []);

    useEffect(() => {
        hideDescPreviewSelection();
    }, [formData.description, hideDescPreviewSelection, descPreviewMode]);

    const copyToClipboard = useCallback(async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar('Copied!', { variant: 'success', autoHideDuration: 1200 });
        } catch {
            enqueueSnackbar('Copy failed', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    const getDescriptionJodit = useCallback(() => {
        const refValue = descriptionEditorRef.current as any;
        // Prefer direct Jodit instance (same as ExternalAssets), fallback to wrapper .editor.
        if (refValue?.selection?.insertHTML) {
            return refValue;
        }
        return refValue?.editor ?? null;
    }, []);

    const applyDescriptionInlineStyle = useCallback(
        (styleType: 'color' | 'background-color', color: string) => {
            const editor = getDescriptionJodit();
            if (!editor?.selection?.insertHTML) {
                return;
            }

            try {
                if (typeof editor.focus === 'function') {
                    editor.focus();
                } else if (editor.editor && typeof editor.editor.focus === 'function') {
                    editor.editor.focus();
                }
            } catch {
                // ignore focus errors
            }

            const selection = editor.selection;
            const selectedHtml = typeof selection.html === 'function' ? selection.html() : '';
            const selectedText = selection.sel?.toString?.() ?? '';
            const hasSelection = Boolean(selectedHtml?.trim() || selectedText.trim());
            const content = (selectedHtml || selectedText).trim();
            const safeContent = content || 'text';

            editor.selection.insertHTML(`<span style="${styleType}: ${color};">${safeContent}</span>`);

            // Keep identical behavior to ExternalAssets: read from editor.value if available.
            const nextDescription = typeof editor.value === 'string' ? editor.value : formData.description;
            const nextData = { ...formData, description: nextDescription };
            setFormData(nextData);

            if (isModalOpen && hasSelection && canAutosaveTask(nextData)) {
                void triggerAutoSaveNow(nextData);
            }
        },
        [formData, getDescriptionJodit, isModalOpen, triggerAutoSaveNow],
    );

    const applyDescriptionQuickColor = useCallback(
        (target: 'text' | 'background', colorKey: TaskDescriptionQuickColorKey) => {
            if (target === 'text') {
                applyDescriptionInlineStyle('color', descriptionQuickPalette.text[colorKey]);
                return;
            }
            applyDescriptionInlineStyle('background-color', descriptionQuickPalette.background[colorKey]);
        },
        [applyDescriptionInlineStyle, descriptionQuickPalette],
    );

    const insertDescriptionTimestamp = useCallback(() => {
        const editor = getDescriptionJodit();
        if (!editor?.selection?.insertHTML) {
            return;
        }

        try {
            if (typeof editor.focus === 'function') {
                editor.focus();
            } else if (editor.editor && typeof editor.editor.focus === 'function') {
                editor.editor.focus();
            }
        } catch {
            // ignore focus errors
        }

        const timestamp = formatNoteEditorTimestamp();
        editor.selection.insertHTML(`<span style="color:#40E0D0; font-size:8px;">${timestamp}</span>`);

        const nextDescription = typeof editor.value === 'string' ? editor.value : formData.description;
        const nextData = { ...formData, description: nextDescription };
        setFormData(nextData);

        if (isModalOpen && canAutosaveTask(nextData)) {
            void triggerAutoSaveNow(nextData);
        }
    }, [formData, getDescriptionJodit, isModalOpen, triggerAutoSaveNow]);

    const handleOpenModal = (task?: Task) => {
        if (task) {
            mergeNotesIntoAvailable((task.notes ?? []).map((n) => ({ id: String(n.id), name: n.name })));
            setSelectedTask(task);
            const nextData = {
                name: task.name,
                description: task.description,
                priority: task.priority?.value ?? DEFAULT_TASK_PRIORITY,
                status: Boolean(task.status),
                noteId: noteId ?? undefined,
                note_ids: task.notes?.map(n => String(n.id)) ?? [],
                project_ids: task.project_ids ?? [],
                due_date: task.due_date ? new Date(task.due_date) : null,
                completed_at: task.completed_at ? new Date(task.completed_at) : null,
                workflow_status: task.workflow_status ?? (task.status ? 'done' : 'todo'),
                assignee: task.assignee?.key ?? null,
                tags: (task.tags ?? []).map((tag) => tag.name),
            };
            setFormData(nextData);
            lastSavedPayloadKeyRef.current = buildAutoSavePayloadKey(nextData);
        } else {
            setSelectedTask(null);
            const defaultProjectIds =
                selectedProjectIdFromQuery.trim().length > 0 ? [selectedProjectIdFromQuery.trim()] : [];
            setFormData({
                name: '',
                description: '',
                priority: DEFAULT_TASK_PRIORITY,
                status: false,
                noteId: noteId ?? undefined,
                note_ids: noteId ? [String(noteId)] : [],
                project_ids: defaultProjectIds,
                due_date: null,
                completed_at: null,
                workflow_status: 'todo',
                assignee: null,
                tags: [],
            });
            lastSavedPayloadKeyRef.current = '';
        }
        setErrors({ name: '', description: '', priority: '' });
        setAutoSaveStatus('idle');
        setDescPreviewMode('rendered');
        setDescPreviewSelection(null);
        setIsModalOpen(true);
    };

    const handleOpenModalRef = useRef(handleOpenModal);
    handleOpenModalRef.current = handleOpenModal;

    const openTaskById = useCallback(
        async (taskId: string) => {
            const id = taskId.trim();
            if (!id) {
                return;
            }
            const fromList = tasks.find((task) => String(task.id) === id);
            if (fromList) {
                handleOpenModalRef.current(fromList);
                return;
            }
            try {
                const data = await taskService.getAll(1, 1, `filters[id][$eq]=${encodeURIComponent(id)}`);
                const remote = data.tasks?.[0];
                if (remote) {
                    handleOpenModalRef.current(remote);
                    return;
                }
                enqueueSnackbar('Task was not found.', { variant: 'warning' });
            } catch {
                enqueueSnackbar('Failed to open task.', { variant: 'error' });
            }
        },
        [enqueueSnackbar, tasks],
    );

    useEffect(() => {
        const handler = (event: Event) => {
            const taskId = (event as CustomEvent<{ taskId?: string }>).detail?.taskId;
            if (taskId) {
                void openTaskById(taskId);
            }
        };
        window.addEventListener(OPEN_TASK_FROM_PROJECTS_EVENT, handler);
        return () => {
            window.removeEventListener(OPEN_TASK_FROM_PROJECTS_EVENT, handler);
        };
    }, [openTaskById]);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }

        const handleDescriptionShortcuts = (event: KeyboardEvent) => {
            const key = event.key.toLowerCase();
            const isQuickColorKey = key === 'g' || key === 'o' || key === 'r';
            const isInsertTimestamp =
                event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey && key === 'q';
            const quickColorMap: Record<'g' | 'o' | 'r', TaskDescriptionQuickColorKey> = {
                g: 'green',
                o: 'orange',
                r: 'red',
            };

            if (isInsertTimestamp && getDescriptionJodit()) {
                event.preventDefault();
                event.stopPropagation();
                insertDescriptionTimestamp();
                return;
            }

            if (
                event.altKey &&
                !event.ctrlKey &&
                !event.metaKey &&
                isQuickColorKey &&
                getDescriptionJodit()
            ) {
                const colorKey = quickColorMap[key as 'g' | 'o' | 'r'];
                event.preventDefault();
                event.stopPropagation();

                if (event.shiftKey) {
                    applyDescriptionQuickColor('text', colorKey);
                    return;
                }

                applyDescriptionQuickColor('background', colorKey);
            }
        };

        window.addEventListener('keydown', handleDescriptionShortcuts);
        return () => {
            window.removeEventListener('keydown', handleDescriptionShortcuts);
        };
    }, [applyDescriptionQuickColor, getDescriptionJodit, insertDescriptionTimestamp, isModalOpen]);

    useEffect(() => {
        const taskId = selectedTaskIdFromQuery;
        if (!taskId) {
            prevTaskIdFromQueryRef.current = '';
            return;
        }
        if (taskId === prevTaskIdFromQueryRef.current) {
            return;
        }
        prevTaskIdFromQueryRef.current = taskId;
        void openTaskById(taskId);
    }, [openTaskById, selectedTaskIdFromQuery]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const wantsNewTask =
            params.get('newTask') === '1' || params.get('newTask')?.toLowerCase() === 'true';
        if (!wantsNewTask) {
            handledNewTaskQueryRef.current = null;
            return;
        }
        if (selectedTaskIdFromQuery) {
            return;
        }
        if (handledNewTaskQueryRef.current === location.search) {
            return;
        }
        handledNewTaskQueryRef.current = location.search;
        handleOpenModal();
        params.delete('newTask');
        const nextSearch = params.toString();
        navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
        // handleOpenModal is stable enough for this one-shot URL trigger; omit from deps to avoid re-firing each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search, navigate, selectedTaskIdFromQuery]);

    const requestCloseModal = useCallback(async () => {
        if (isClosingModal) {
            return;
        }
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }

        const key = buildAutoSavePayloadKey(formData);
        const dirty = canAutosaveTask(formData) && key !== lastSavedPayloadKeyRef.current;

        if (dirty) {
            setIsClosingModal(true);
            const saved = await persistTask(formData, {
                closeOnSuccess: false,
                showSuccessToast: false,
                isAutoSave: true,
            });
            setIsClosingModal(false);

            if (!saved) {
                enqueueSnackbar(
                    t('tasks.updateError', { defaultValue: 'Failed to save before close.' }),
                    { variant: 'error' },
                );
                return;
            }
        }

        closeModalImmediately();
    }, [buildAutoSavePayloadKey, closeModalImmediately, enqueueSnackbar, formData, isClosingModal, persistTask, t]);

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }
        setTaskIsEdit(false);
        void persistTask(formData, { closeOnSuccess: true, showSuccessToast: true, isAutoSave: false });
    };

    useEffect(() => {
        if (taskIsEdit) handleSubmit()
    }, [taskIsEdit]);

    useEffect(() => {
        if (!noteId) {
            localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(columnFilters));
        }
    }, [columnFilters, noteId]);

    useEffect(() => {
        if (!noteId && !isMobile) {
            localStorage.setItem(
                LOCAL_STORAGE_COLUMN_VISIBILITY_KEY,
                JSON.stringify(columnVisibility)
            );
        }
    }, [columnVisibility, noteId, isMobile]);

    const handleDelete = (task: Task) => {
        setSelectedTask(task);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (selectedTask) {
            deleteMutation.mutate(selectedTask.id);
        }
    };

    // Merge search results with already-selected notes so selected chips remain visible
    const notesAutocompleteOptions = useMemo(() => {
        const merged = [...availableNotes];
        const existingIds = new Set(availableNotes.map((n) => String(n.id)));
        for (const n of notesSearchOptions) {
            if (!existingIds.has(String(n.id))) merged.push(n);
        }
        return merged;
    }, [availableNotes, notesSearchOptions]);

    const selectedNotes = useMemo(() => {
        const setIds = new Set((formData.note_ids ?? []).map((id) => String(id)));
        return notesAutocompleteOptions.filter((n) => setIds.has(String(n.id)));
    }, [notesAutocompleteOptions, formData.note_ids]);

    const selectedProjects = useMemo(() => {
        const setIds = new Set(formData.project_ids);
        return availableProjects.filter(project => setIds.has(project.id));
    }, [availableProjects, formData.project_ids]);

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    {t('tasks.title')}
                </Typography>
                <Button variant="contained" onClick={() => handleOpenModal()}>
                    {t('tasks.createNew')}
                </Button>
            </Box>

            <Dialog
                open={isModalOpen}
                onClose={() => {
                    void requestCloseModal();
                }}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        width: '80vw',
                        maxWidth: '80vw',
                    },
                }}
            >
                <DialogTitle>
                    {selectedTask ? t('tasks.editTask') : t('tasks.createTask')}
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        {/* ── Left column: meta fields ── */}
                        <Stack spacing={2}>
                            <MarkerHighlightTextField
                                label={t('tasks.name')}
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                error={!!errors.name}
                                helperText={errors.name}
                                fullWidth
                                required
                            />

                            <FormControl fullWidth error={!!errors.priority} required>
                                <InputLabel>{t('tasks.priority')}</InputLabel>
                                <Select
                                    value={formData.priority ?? ''}
                                    label={t('tasks.priority')}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value ? parseInt(String(e.target.value)) : null })}
                                >
                                    {taskPriorities.map((priority) => (
                                        <MenuItem key={priority.value} value={priority.value}>
                                            {priority.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.priority && <Typography color="error" variant="caption">{errors.priority}</Typography>}
                            </FormControl>

                            <FormControl component="fieldset">
                                <RadioGroup
                                    value={formData.status}
                                    onChange={(e) => {
                                        const status = e.target.value === 'true';
                                        const current = formData.workflow_status ?? (formData.status ? 'done' : 'todo');
                                        const workflow_status: TaskWorkflowStatus = status
                                            ? 'done'
                                            : (current === 'done' ? 'todo' : current);
                                        setFormData({ ...formData, status, workflow_status });
                                    }}
                                >
                                    <FormControlLabel value={true} control={<Radio />} label={t('tasks.active')} />
                                    <FormControlLabel value={false} control={<Radio />} label={t('tasks.inactive')} />
                                </RadioGroup>
                            </FormControl>

                            <WorkflowStatusSelect
                                value={formData.workflow_status ?? (formData.status ? 'done' : 'todo')}
                                onChange={(workflow_status) => {
                                    const status = workflow_status === 'done';
                                    if (status && !formData.status) {
                                        startConfetti();
                                    }
                                    setFormData({ ...formData, workflow_status, status });
                                }}
                            />

                            <AssigneeSelect
                                value={formData.assignee ?? null}
                                onChange={(assignee) => setFormData({ ...formData, assignee })}
                            />

                            <TagsInput
                                value={formData.tags ?? []}
                                onChange={(tags) => setFormData({ ...formData, tags })}
                            />

                            {selectedTask?.created_by && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="caption" color="text.secondary">{t('workflow.createdBy')}</Typography>
                                    <ActorChip actor={selectedTask.created_by} />
                                </Stack>
                            )}

                            <DateTimePicker
                                label={t('tasks.dueDate')}
                                value={formData.due_date}
                                onChange={(newValue) => setFormData({ ...formData, due_date: newValue })}
                                format={APP_DATETIME_FORMAT}
                                slotProps={{ textField: { fullWidth: true } }}
                                ampm={false}
                            />

                            {selectedTask && (
                                <DateTimePicker
                                    label={t('tasks.completedAt')}
                                    value={formData.completed_at}
                                    format={APP_DATETIME_FORMAT}
                                    disabled={true}
                                    slotProps={{ textField: { fullWidth: true } }}
                                    ampm={false}
                                />
                            )}

                            <Divider />

                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={notesAutocompleteOptions}
                                getOptionLabel={(option) => option?.name || ''}
                                value={selectedNotes}
                                inputValue={notesSearchInput}
                                onChange={(_e, val) => setFormData({ ...formData, note_ids: (val || []).map((n) => String(n.id)) })}
                                filterSelectedOptions
                                filterOptions={(x) => x}
                                loading={notesSearchLoading}
                                isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
                                onInputChange={(_e, value) => setNotesSearchInput(value)}
                                clearOnBlur={false}
                                autoSelect={false}
                                freeSolo={false}
                                renderOption={(props, option, { selected }) => {
                                    const { key, ...rest } = props as any;
                                    return (
                                        <li key={key} {...rest}>
                                            <Checkbox checked={selected} />
                                            <ListItemText primary={option.name} />
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label={t('notes.title')} />
                                )}
                            />

                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={availableProjects}
                                getOptionLabel={(option) => option?.name || ''}
                                value={selectedProjects}
                                onChange={(_e, val) => setFormData({ ...formData, project_ids: (val || []).map(project => project.id) })}
                                filterSelectedOptions
                                isOptionEqualToValue={(option, val) => option.id === val.id}
                                renderOption={(props, option, { selected }) => {
                                    const { key, ...rest } = props as any;
                                    return (
                                        <li key={key} {...rest}>
                                            <Checkbox checked={selected} />
                                            <ListItemText primary={option.name} />
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Projects" />
                                )}
                            />
                        </Stack>

                        {/* ── Right column: description editor + preview ── */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {t('tasks.description')}
                            </Typography>
                            <Stack spacing={1} sx={{ mb: 1.25 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                        Quick actions
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={insertDescriptionTimestamp}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Insert time (Alt+Q)
                                    </Button>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                        Text color
                                    </Typography>
                                    {descriptionQuickColorOptions.map((item) => (
                                        <Button
                                            key={`desc-text-${item.key}`}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => applyDescriptionQuickColor('text', item.key)}
                                            startIcon={
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        bgcolor: descriptionQuickPalette.text[item.key],
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                    }}
                                                />
                                            }
                                            sx={{
                                                textTransform: 'none',
                                                color: descriptionQuickPalette.text[item.key],
                                                borderColor: descriptionQuickPalette.text[item.key],
                                            }}
                                        >
                                            {item.label} (Alt+Shift+{item.hotkey})
                                        </Button>
                                    ))}
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                        Background
                                    </Typography>
                                    {descriptionQuickColorOptions.map((item) => (
                                        <Button
                                            key={`desc-bg-${item.key}`}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => applyDescriptionQuickColor('background', item.key)}
                                            startIcon={
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        bgcolor: descriptionQuickPalette.background[item.key],
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                    }}
                                                />
                                            }
                                            sx={{
                                                textTransform: 'none',
                                                bgcolor: descriptionQuickPalette.background[item.key],
                                                borderColor: descriptionQuickPalette.text[item.key],
                                                color: '#1f1f1f',
                                                '&:hover': {
                                                    bgcolor: descriptionQuickPalette.background[item.key],
                                                    filter: 'brightness(0.98)',
                                                },
                                            }}
                                        >
                                            {item.label} (Alt+{item.hotkey})
                                        </Button>
                                    ))}
                                </Stack>
                            </Stack>
                            {errors.description && (
                                <Typography color="error" variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                    {errors.description}
                                </Typography>
                            )}
                            <JoditEditor
                                ref={descriptionEditorRef}
                                value={formData.description}
                                config={descriptionEditorConfig}
                                onBlur={(content) => {
                                    const next = { ...formData, description: content };
                                    setFormData(next);
                                    if (isModalOpen && canAutosaveTask(next)) {
                                        void triggerAutoSaveNow(next);
                                    }
                                }}
                                onChange={() => {
                                    // Keep Jodit internally uncontrolled while typing to prevent cursor jumps.
                                }}
                            />

                            {/* HTML Preview */}
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                                        HTML Preview
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button size="small" variant="text" onClick={() => setIsDescLargeOpen(true)}>
                                            Open Large
                                        </Button>
                                        <Button
                                            size="small"
                                            variant={descPreviewMode === 'rendered' ? 'contained' : 'outlined'}
                                            onClick={() => setDescPreviewMode('rendered')}
                                        >
                                            Rendered
                                        </Button>
                                        <Button
                                            size="small"
                                            variant={descPreviewMode === 'raw' ? 'contained' : 'outlined'}
                                            onClick={() => setDescPreviewMode('raw')}
                                        >
                                            Raw HTML
                                        </Button>
                                    </Box>
                                </Box>
                                {descPreviewMode === 'rendered' ? (
                                    <Box
                                        ref={descPreviewRef}
                                        onMouseUp={updateDescPreviewSelection}
                                        onKeyUp={updateDescPreviewSelection}
                                        onMouseDown={() => hideDescPreviewSelection()}
                                        sx={{
                                            position: 'relative',
                                            minHeight: 120,
                                            maxHeight: 260,
                                            overflowY: 'auto',
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            bgcolor: 'background.paper',
                                            '& a': { color: 'primary.main' },
                                        }}
                                    >
                                        {descPreviewSelection && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<ContentCopyIcon fontSize="small" />}
                                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    void copyToClipboard(descPreviewSelection.text);
                                                    hideDescPreviewSelection();
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: Math.max(descPreviewSelection.top, 8),
                                                    left: `clamp(8px, calc(${descPreviewSelection.left}px - 56px), calc(100% - 104px))`,
                                                    zIndex: 2,
                                                    minWidth: 88,
                                                    boxShadow: 3,
                                                    textTransform: 'none',
                                                }}
                                            >
                                                Copy
                                            </Button>
                                        )}
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: (formData.description ?? '').trim() || '<span style="color:#888;">No content to preview.</span>',
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <TextField
                                        value={formData.description ?? ''}
                                        fullWidth
                                        multiline
                                        minRows={8}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem',
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                    {selectedTask?.id && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 2 }} />
                            <TaskComments
                                taskId={String(selectedTask.id)}
                                onChanged={() => {
                                    void queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {isModalOpen && (
                        <Typography
                            variant="caption"
                            sx={{
                                mr: 'auto',
                                px: 1.25,
                                py: 0.4,
                                borderRadius: 1,
                                fontWeight: 600,
                                color: autoSaveStatus === 'error' ? 'error.dark' : 'text.primary',
                                bgcolor:
                                    autoSaveStatus === 'pending'
                                        ? 'warning.light'
                                        : autoSaveStatus === 'saving'
                                            ? 'info.light'
                                            : autoSaveStatus === 'saved'
                                                ? 'success.light'
                                                : autoSaveStatus === 'error'
                                                    ? 'error.light'
                                                    : 'action.hover',
                            }}
                        >
                            {autoSaveStatus === 'pending' && 'Autosave pending...'}
                            {autoSaveStatus === 'saving' && 'Autosaving...'}
                            {autoSaveStatus === 'saved' && 'Saved ✓'}
                            {autoSaveStatus === 'error' && 'Autosave failed'}
                            {autoSaveStatus === 'idle' && 'Autosave ready'}
                        </Typography>
                    )}
                    <Button
                        onClick={() => {
                            void requestCloseModal();
                        }}
                        disabled={createMutation.isPending || updateMutation.isPending || isClosingModal}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={createMutation.isPending || updateMutation.isPending || isClosingModal}
                    >
                        {selectedTask ? t('tasks.save') : t('tasks.create')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <DialogTitle>{t('tasks.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('tasks.deleteConfirmation')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteModalOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        {t('common.yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Open Large — full-screen HTML preview of description */}
            <Dialog
                open={isDescLargeOpen}
                onClose={() => setIsDescLargeOpen(false)}
                maxWidth={false}
                fullWidth
                PaperProps={{ sx: { width: '90vw', maxWidth: '90vw' } }}
            >
                <DialogTitle>Description — HTML Preview (Large)</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            minHeight: '60vh',
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            overflowY: 'auto',
                            '& a': { color: 'primary.main' },
                        }}
                        dangerouslySetInnerHTML={{
                            __html: (formData.description ?? '').trim() || '<span style="color:#888;">No content to preview.</span>',
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDescLargeOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <MaterialReactTable table={table} />

            {runConfetti && (
                <Confetti
                    width={width}
                    height={height}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        pointerEvents: "none",
                        zIndex: 9999,
                    }}
                    recycle={false}
                    numberOfPieces={200}
                    onConfettiComplete={() => setRunConfetti(false)}
                />
            )}
        </Box>
    );
};
